/**
 * StornoKorrekturModal — Feb 2026
 * Zwei Modi:
 *   mode="storno"     → einfaches Reason-Feld + Confirm.
 *   mode="korrektur"  → Reason-Feld + editierbare Positionen + Confirm.
 *                       Erstellt Storno + Ersatz-Rechnung als Entwurf.
 *
 * Props:
 *   open               Boolean
 *   mode               "storno" | "korrektur"
 *   invoice            { id, invoice_number, items, total, currency, country }
 *   onClose            () => void
 *   onSuccess(result)  callback nach erfolgreichem API-Call (result = response.data)
 */
import React, { useState, useMemo } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { AlertTriangle, FileWarning, Loader2 } from "lucide-react";

const fmtMoney = (n, currency = "EUR") => {
  const v = Number(n || 0);
  return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(v);
};

export default function StornoKorrekturModal({ open, mode, invoice, onClose, onSuccess }) {
  // Initial values are computed once at mount; parent forces remount via `key`
  // when invoice/mode changes, which keeps useState in sync without setState-in-effect.
  const isKorrektur = mode === "korrektur";
  const [reason, setReason] = useState("");
  const [items, setItems] = useState(() => (
    isKorrektur && invoice?.items
      ? invoice.items.map((it) => ({
          description: it.description || "",
          quantity: Number(it.quantity || 1),
          unit_price: Number(it.unit_price || 0),
          tax_rate: Number(it.tax_rate || 0),
          total: Number(it.total || 0),
        }))
      : []
  ));
  const [busy, setBusy] = useState(false);

  const recalcItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, ...patch };
      const qty = Number(next.quantity || 0);
      const up = Number(next.unit_price || 0);
      next.total = Math.round(qty * up * 100) / 100;
      return next;
    }));
  };

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + Number(it.total || 0), 0);
    const tax = items.reduce((s, it) => s + Number(it.total || 0) * Number(it.tax_rate || 0) / 100, 0);
    return { subtotal, tax, total: subtotal + tax };
  }, [items]);

  if (!invoice) return null;
  const currency = invoice.currency || "EUR";

  const handleConfirm = async () => {
    if (mode === "korrektur" && !reason.trim()) {
      toast.error("Korrekturgrund ist Pflicht");
      return;
    }
    if (mode === "korrektur" && (!items.length || items.some((i) => !i.description?.trim() || Number(i.total) === 0))) {
      toast.error("Mindestens eine Position mit Beschreibung und Betrag erforderlich");
      return;
    }
    setBusy(true);
    try {
      if (mode === "storno") {
        const res = await api.post(`/invoices/${invoice.id}/storno`, { reason: reason.trim() || null });
        toast.success(`Stornorechnung ${res.data.invoice_number} erstellt`);
        onSuccess?.(res.data);
      } else {
        const res = await api.post(`/invoices/${invoice.id}/korrigieren`, {
          reason: reason.trim(),
          items: items.map((it) => ({
            description: it.description,
            quantity: Number(it.quantity),
            unit_price: Number(it.unit_price),
            tax_rate: Number(it.tax_rate),
            total: Number(it.total),
          })),
        });
        toast.success(
          `Storno ${res.data?.storno?.invoice_number} + Ersatzrechnung ${res.data?.replacement?.invoice_number} erstellt`
        );
        onSuccess?.(res.data);
      }
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message) || detail || err?.message || "Vorgang fehlgeschlagen";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const isStorno = mode === "storno";
  const title = isStorno ? "Stornorechnung erstellen" : "Korrekturrechnung erstellen";
  const desc = isStorno
    ? `Erzeugt eine neue Stornorechnung mit negativen Beträgen, die ${invoice.invoice_number} aufhebt. Beide Belege bleiben dauerhaft im Audit-Trail erhalten.`
    : `Storniert ${invoice.invoice_number} und erzeugt eine neue Ersatzrechnung mit korrigierten Positionen als Entwurf.`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid={`${mode}-modal`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isStorno ? <FileWarning className="h-5 w-5 text-rose-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">{desc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Original info */}
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Original-Rechnung:</span>
              <span className="font-semibold text-slate-900">{invoice.invoice_number}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Brutto-Betrag:</span>
              <span className="font-medium tabular-nums">{fmtMoney(invoice.total, currency)}</span>
            </div>
            {invoice.tax_scenario && invoice.tax_scenario !== "standard" && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-slate-600">Steuerart Original:</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                  {invoice.tax_scenario === "reverse_charge" && "Reverse Charge"}
                  {invoice.tax_scenario === "intra_community" && "Innergem. Lieferung"}
                  {invoice.tax_scenario === "export" && "Export"}
                  {invoice.tax_scenario === "small_business" && "Kleinunternehmer"}
                  {invoice.tax_scenario === "exempt" && "Steuerbefreit"}
                </span>
              </div>
            )}
          </div>

          {/* Hint when original had a special scenario */}
          {!isStorno && invoice.tax_scenario && invoice.tax_scenario !== "standard" && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <strong>Hinweis:</strong> Die Original-Rechnung war eine Spezial-Steuerart (0 % USt).
              Wenn du in den Positionen jetzt einen USt-Satz &gt; 0 % einträgst, wird die Ersatzrechnung
              automatisch als reguläre Standard-Rechnung mit Steuerausweis erstellt.
            </div>
          )}

          {/* Reason */}
          <div>
            <Label htmlFor="storno-reason" className="text-sm font-medium">
              {isStorno ? "Grund (optional, erscheint auf der Stornorechnung)" : "Korrekturgrund (Pflicht — erscheint auf beiden PDFs)"}
            </Label>
            <Textarea
              id="storno-reason"
              data-testid={`${mode}-reason-input`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isStorno ? "z. B. Falscher Kunde, doppelte Verrechnung …" : "z. B. USt-Satz war 7 % statt 19 %, Position X nachträglich rabattiert …"}
              rows={2}
              className="mt-1"
            />
          </div>

          {/* Items editor — only korrektur */}
          {!isStorno && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Positionen der Ersatzrechnung</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setItems((p) => [...p, { description: "", quantity: 1, unit_price: 0, tax_rate: invoice.country === "AT" ? 20 : invoice.country === "CH" ? 8.1 : 19, total: 0 }])}
                  data-testid="korrektur-add-item"
                >
                  + Position
                </Button>
              </div>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 rounded border border-slate-200">
                    <div className="col-span-12 md:col-span-5">
                      <Label className="text-xs text-slate-500">Beschreibung</Label>
                      <Input
                        value={it.description}
                        onChange={(e) => recalcItem(idx, { description: e.target.value })}
                        data-testid={`korrektur-item-${idx}-description`}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-2">
                      <Label className="text-xs text-slate-500">Menge</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={it.quantity}
                        onChange={(e) => recalcItem(idx, { quantity: parseFloat(e.target.value || 0) })}
                        data-testid={`korrektur-item-${idx}-quantity`}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-xs text-slate-500">Einzelpreis</Label>
                      <Input
                        type="number" step="0.01"
                        value={it.unit_price}
                        onChange={(e) => recalcItem(idx, { unit_price: parseFloat(e.target.value || 0) })}
                        data-testid={`korrektur-item-${idx}-price`}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1">
                      <Label className="text-xs text-slate-500">USt %</Label>
                      <Input
                        type="number" step="0.1" min="0" max="30"
                        value={it.tax_rate}
                        onChange={(e) => recalcItem(idx, { tax_rate: parseFloat(e.target.value || 0) })}
                        data-testid={`korrektur-item-${idx}-tax`}
                      />
                    </div>
                    <div className="col-span-2 md:col-span-2 text-right">
                      <Label className="text-xs text-slate-500">Summe</Label>
                      <div className="text-sm tabular-nums font-medium pt-1">{fmtMoney(it.total, currency)}</div>
                    </div>
                  </div>
                ))}
                {!items.length && (
                  <p className="text-xs text-slate-500 italic">Noch keine Positionen — klicke &bdquo;+ Position&ldquo;.</p>
                )}
              </div>

              {/* Totals preview */}
              <div className="mt-3 flex justify-end gap-6 text-sm">
                <div>
                  <span className="text-slate-500">Netto: </span>
                  <span className="font-medium tabular-nums">{fmtMoney(totals.subtotal, currency)}</span>
                </div>
                <div>
                  <span className="text-slate-500">USt: </span>
                  <span className="font-medium tabular-nums">{fmtMoney(totals.tax, currency)}</span>
                </div>
                <div className="font-semibold">
                  <span className="text-slate-700">Brutto: </span>
                  <span className="tabular-nums">{fmtMoney(totals.total, currency)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid={`${mode}-cancel`}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy}
            className={isStorno ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}
            data-testid={`${mode}-confirm`}
          >
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isStorno ? "Stornorechnung erstellen" : "Korrekturrechnung erstellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
